import React from 'react';
import { Task } from '@/context/TaskContext';
import { Clock, CheckCircle } from 'lucide-react';
import { formatTaskTime } from './taskCardUtils';
import { TaskCardBadge } from './TaskCardBadge';
import { getCardBaseClasses, getCardHeaderClasses, getCardTitleClasses, getCardDateClasses, getCardMetadataClasses } from '@/components/ui/shared-card-styles';
import { cn } from '@/lib/utils';
import { PriorityDisplay } from './PriorityDisplay';
import { TaskActiveDot } from "./TaskActiveDot";
import { useTaskDetailModal } from './TaskDetailModalContext';

interface TaskCardFullProps {
  task: Task;
  onSelect: (id: string) => void;
  isActive: boolean;
}

// Helper: return just the color for the left border
const getStatusBarColor = (status: string): string => {
  switch (status) {
    case 'to-do': return 'bg-yellow-500 dark:bg-yellow-400';
    case 'in-progress': return 'bg-blue-500 dark:bg-blue-400';
    case 'done': return 'bg-green-500 dark:bg-green-400';
    default: return 'bg-gray-500 dark:bg-gray-400';
  }
};

export const TaskCardFull: React.FC<TaskCardFullProps> = ({ 
  task, 
  onSelect, 
  isActive 
}) => {
  const formattedDate = task.dateCreated 
    ? task.dateCreated.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : new Date(task.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

  const formattedTime = formatTaskTime(task.totalTimeSpent);

  // Single click = select (DO NOT open detail), double click = open modal
  const { open } = useTaskDetailModal();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(task.id); // Only select, do NOT open modal
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    open(task); // Show detail modal
  };

  return (
    <div 
      className={cn(
        getCardBaseClasses(isActive),
        "relative overflow-hidden"
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Colored edge with straight inner (left) edge */}
      <div
        className={cn(
          "absolute left-0 top-0 h-full w-1.5 sm:w-2",
          getStatusBarColor(task.status),
          "rounded-none"
        )}
        aria-hidden="true"
      />
      {isActive && <TaskActiveDot />}

      <div className={getCardHeaderClasses()}>
        <h3 className={getCardTitleClasses()}>{task.title}</h3>
      </div>
      <p className={getCardDateClasses()}>Created on {formattedDate}</p>
      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 line-clamp-2 pl-2">{task.detail || task.description || ""}</p>
      <div className={cn(getCardMetadataClasses(), "mt-3 pt-2 border-t border-gray-100 dark:border-gray-700")}>
        <div className="flex items-center">
          <CheckCircle size={16} className="text-gray-500 dark:text-gray-400 mr-1" />
          <span className="text-sm text-gray-500 dark:text-gray-400">{task.pomodorosCompleted} pomodoros</span>
        </div>
        <div className="flex items-center">
          <Clock size={16} className="text-gray-500 dark:text-gray-400 mr-1" />
          <span className="text-sm text-gray-500 dark:text-gray-400">{formattedTime}</span>
        </div>
      </div>
      {task.priority_quadrant && (
        <PriorityDisplay quadrant={task.priority_quadrant} />
      )}
    </div>
  );
};
